import { ReactElement } from 'react'
import { Typography, useTheme } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import useLoadsCombinedStore from '../../stores/useLoadsCombinedStore'
import LoadResultsToolbar from './LoadResultsToolbar'

interface Props {
  columns: GridColDef[]
  rows: { id: number }[]
  emptyMessage: string
  /** Optional note above the table (e.g. what the positions are relative to). */
  description?: string
}

/**
 * One Einwirkungen table in the look of the element load tables: compact data grid with sortable
 * columns, header filters and the shared "Lasten zusammenfassen" toolbar. Shared by the tensile
 * and shear anchor detail panes so the two stay visually identical.
 */
const LoadResultsGrid = ({ columns, rows, emptyMessage, description }: Props): ReactElement => {
  const theme = useTheme()
  // Same app-wide "Lasten zusammenfassen" state as the element load tables.
  const loadsCombined = useLoadsCombinedStore(state => state.loadsCombined)
  const setLoadsCombined = useLoadsCombinedStore(state => state.setLoadsCombined)

  const gridSx = {
    border: 0,
    fontFamily: 'Roboto',
    backgroundColor: 'transparent',
    '& .MuiDataGrid-main': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader .MuiTypography-root': {
      fontSize: 12,
      color: theme.palette.text.secondary,
      fontWeight: 400,
      letterSpacing: 0,
    },
    '& .MuiDataGrid-cell': {
      fontSize: 12,
      color: theme.palette.text.primary,
      fontFamily: 'Roboto',
      fontVariantNumeric: 'tabular-nums',
      px: 0.75,
      borderColor: theme.palette.divider,
    },
    '& .MuiDataGrid-columnHeader': {
      px: 0.75,
    },
    '& .MuiDataGrid-columnSeparator': {
      opacity: 0,
    },
    '& .MuiDataGrid-toolbarContainer': {
      minHeight: 28,
      p: 0,
      pb: 0.75,
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
      minHeight: 24,
      px: 0.75,
      py: 0.25,
      fontSize: 11,
      lineHeight: 1.2,
    },
    '& .MuiDataGrid-toolbarContainer .MuiInputBase-root': {
      fontSize: 11,
    },
    '& .MuiDataGrid-toolbarContainer .MuiInputBase-input': {
      py: 0.25,
    },
    '& .MuiDataGrid-headerFilterRow .MuiInputBase-root': {
      fontSize: 11,
    },
    '& .MuiDataGrid-headerFilterRow .MuiInputBase-input': {
      py: 0.5,
      px: 0.5,
    },
    // Reduce visual noise in the filter bar without shrinking the row height.
    '& .MuiDataGrid-headerFilterRow .MuiInputBase-input::placeholder': {
      opacity: 0,
    },
  } as const

  return (
    <>
      {description ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 0.5 }}>
          {description}
        </Typography>
      ) : null}
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <DataGridPremium
          rows={rows}
          columns={columns}
          // Same header height as the element load tables (PointLoadTable etc.).
          columnHeaderHeight={75}
          headerFilters
          // Same filter-row height as the element load tables (PointLoadTable etc.).
          headerFilterHeight={75}
          density="compact"
          hideFooter
          autoHeight
          initialState={{
            sorting: {
              sortModel: [{ field: 'loadCase', sort: 'asc' }],
            },
          }}
          disableRowSelectionOnClick
          disableRowGrouping
          // @ts-expect-error custom toolbar props (same pattern as the element load tables)
          slots={{ toolbar: LoadResultsToolbar }}
          slotProps={{
            toolbar: {
              // @ts-expect-error custom toolbar props (same pattern as the element load tables)
              loadsCombined,
              onLoadsCombinedChange: setLoadsCombined,
            },
          }}
          sx={gridSx}
        />
      )}
    </>
  )
}

export default LoadResultsGrid
